import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Phone, ArrowRight } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import CountryCodeSelect from '../../components/ui/CountryCodeSelect';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Alert from '../../components/ui/Alert';
import Loading from '../../components/ui/Loading';
import { formatPhoneNumber, isValidPhone, isValidOTPCode } from '../../utils';
import type { LoginFormData, VerifyFormData } from '../../types';



// Validation schemas
const phoneSchema = yup.object({
  phone: yup
    .string()
    .required('El número de teléfono es requerido')
    .test('valid-phone', 'Formato de teléfono inválido', (value) => 
      value ? isValidPhone(value) : false
    ),
});

const codeSchema = yup.object({
  code: yup
    .string()
    .required('El código es requerido')
    .test('valid-code', 'El código debe tener 6 dígitos', (value) => 
      value ? isValidOTPCode(value) : false
    ),
});

const Login: React.FC = () => {
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+57');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operatorName, setOperatorName] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for auto-login from WhatsApp
  useEffect(() => {
    const phone = searchParams.get('phone');
    
    if (phone && isValidPhone(phone)) {
      const fullPhone = countryCode + phone;
      setPhoneNumber(fullPhone);
      phoneForm.setValue('phone', phone);
      const sendAutoCode = async () => {
        setLoading(true);
        setError(null);

        try {
          const response = await authService.sendLoginCode({ phone: fullPhone });
          
          if (response.status === 'success') {
            setOperatorName(response.operatorName || '');
            setStep('code');
            
            // Clear URL params for clean UI
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('phone');
            window.history.replaceState({}, '', newUrl.pathname);
            
          } else {
            setError(response.message);
          }
        } catch (err: any) {
          console.error('Error auto-sending code:', err);
          setError(
            err.response?.data?.message || 
            'Error al enviar el código por WhatsApp. Verifica tu número de teléfono.'
          );
        } finally {
          setLoading(false);
        }
      };
      
      sendAutoCode();
    }
  }, [searchParams]);

  const phoneForm = useForm<LoginFormData>({
    resolver: yupResolver(phoneSchema),
    defaultValues: {
      phone: '',
    },
  });

  const codeForm = useForm<VerifyFormData>({
    resolver: yupResolver(codeSchema),
  });

  const handleSendCode = async (data: LoginFormData) => {
    setLoading(true);
    setError(null);

    const fullPhone = countryCode + data.phone;
    try {
      const response = await authService.sendLoginCode({ phone: fullPhone });

      if (response && response.status === 'success') {
        setPhoneNumber(fullPhone);
        setOperatorName(response.operatorName || '');
        setStep('code');
      } else {
        setError(response?.message || 'Error desconocido');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 
        'Error al enviar el código por WhatsApp. Verifica tu número de teléfono.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (data: VerifyFormData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.verifyLoginCode({
        phone: phoneNumber,
        code: data.code,
      });

      if (response.status === 'success' && response.operator) {
        // Sin token no se crea sesión. Antes se entraba igual y el usuario quedaba
        // "autenticado" sin poder hablar con la API: desde que las rutas del acta
        // exigen token, esa sesión solo produce errores de carga. Si la API dice
        // success y no manda token, el roto es el backend y hay que verlo, no
        // dejar al usuario adentro con una sesión que no sirve.
        if (!response.token) {
          setError('No se pudo iniciar sesión. Intenta de nuevo o contacta a soporte.');
          return;
        }
        localStorage.setItem('auth_token', response.token);
        login(response.operator);
        // Respeta ?redirect=... si vino del deep link / QR landing
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get('redirect');
        navigate(redirect && redirect.startsWith('/') ? redirect : '/dashboard');
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      console.error('Error verifying code:', err);
      setError(
        err.response?.data?.message || 
        'Código incorrecto. Intenta nuevamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setError(null);
    codeForm.reset();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loading size="lg" text="Procesando..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-3 sm:px-4">
      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 sm:h-20 sm:w-20 mb-4">
            <img 
              src="/logo-retri-blue.png" 
              alt="RETRI Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <h2 className="mt-4 sm:mt-6 text-2xl sm:text-3xl font-bold text-gray-900">
            RETRI
          </h2>
          <p className="mt-2 text-xs sm:text-sm text-gray-600">
            Registro de ejecución
          </p>
        </div>

        <Card>
          <CardHeader className="pb-4 sm:pb-6">
            <CardTitle className="text-center text-lg sm:text-xl">
              {step === 'phone' ? 'Iniciar Sesión' : 'Verificar Código'}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
            {error && (
              <Alert variant="error">
                {error}
              </Alert>
            )}

            {step === 'phone' && (
              <>
                {loading && searchParams.get('phone') ? (
                  <div className="text-center space-y-4">
                    <Loading size="md" text="Enviando código automáticamente..." />
                    <p className="text-sm text-gray-600">
                      Detectamos tu número desde el enlace
                    </p>
                  </div>
                ) : (
                  <form onSubmit={phoneForm.handleSubmit(handleSendCode)} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Número de teléfono
                      </label>
                      <div className="flex gap-2">
                        <CountryCodeSelect
                          value={countryCode}
                          onChange={setCountryCode}
                          className="h-12 sm:h-14"
                        />
                        <Input
                          {...phoneForm.register('phone')}
                          type="tel"
                          placeholder="Ej: 3001234567"
                          error={phoneForm.formState.errors.phone?.message}
                          className="text-center text-base sm:text-lg h-12 sm:h-14"
                        />
                      </div>
                    </div>
                    
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full h-12 sm:h-14 text-sm sm:text-base"
                      disabled={loading}
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      <span className="hidden sm:inline">Enviar Código por WhatsApp</span>
                      <span className="inline sm:hidden">Enviar por WhatsApp</span>
                    </Button>
                  </form>
                )}
              </>
            )}

            {step === 'code' && (
              <div className="space-y-4">
                <div className="text-center space-y-2">
                  <p className="text-sm text-gray-600">
                    Código enviado por WhatsApp a
                  </p>
                  <p className="font-medium text-retri-blue">
                    {formatPhoneNumber(phoneNumber)}
                  </p>
                  {operatorName && (
                    <p className="text-sm text-gray-600">
                      Hola, <span className="font-medium">{operatorName}</span>
                    </p>
                  )}
                </div>

                <form onSubmit={codeForm.handleSubmit(handleVerifyCode)} className="space-y-4">
                  <Input
                    {...codeForm.register('code')}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    label="Código de verificación"
                    placeholder="123456"
                    error={codeForm.formState.errors.code?.message}
                    className="text-center text-xl sm:text-2xl tracking-widest h-12 sm:h-16"
                    maxLength={6}
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');
                    }}
                  />
                  
                  <div className="space-y-2 sm:space-y-3">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full h-12 sm:h-14"
                      disabled={loading}
                    >
                      <ArrowRight className="mr-2 h-4 w-4" />
                      Verificar Código
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      className="w-full h-10 sm:h-12"
                      onClick={handleBack}
                    >
                      <span className="hidden sm:inline">Cambiar número</span>
                      <span className="inline sm:hidden">Cambiar</span>
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2025 RETRI. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
